import { reportAPI } from '../../../services/api';
import { ReportTarget } from '../../../types/api';

export const REPORT_TARGETS_CACHE_KEY = 'report_targets_v1';

const REPORT_TARGETS_CACHE_TTL_MS = 5 * 60 * 1000;

type ReportTargetsCachePayload = {
  targets: ReportTarget[];
  cachedAt: number;
};

let inFlightReportTargetsRequest: Promise<ReportTarget[]> | null = null;

const sortTargets = (targets: ReportTarget[]) => targets.slice().sort((a, b) => {
  if (a.type === 'group' && b.type !== 'group') {
    return -1;
  }
  if (a.type !== 'group' && b.type === 'group') {
    return 1;
  }
  return a.label.localeCompare(b.label, 'sv');
});

const isValidTarget = (value: unknown): value is ReportTarget => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const candidate = value as Partial<ReportTarget>;
  return typeof candidate.id === 'string'
    && (candidate.type === 'company' || candidate.type === 'group')
    && typeof candidate.label === 'string';
};

const parseCachedPayload = (): ReportTargetsCachePayload | null => {
  const cached = sessionStorage.getItem(REPORT_TARGETS_CACHE_KEY);
  if (!cached) {
    return null;
  }

  try {
    const parsed = JSON.parse(cached) as Partial<ReportTargetsCachePayload>;
    if (!Array.isArray(parsed.targets) || typeof parsed.cachedAt !== 'number') {
      sessionStorage.removeItem(REPORT_TARGETS_CACHE_KEY);
      return null;
    }

    const targets = parsed.targets.filter(isValidTarget);
    if (targets.length !== parsed.targets.length) {
      sessionStorage.removeItem(REPORT_TARGETS_CACHE_KEY);
      return null;
    }

    return {
      targets: sortTargets(targets),
      cachedAt: parsed.cachedAt,
    };
  } catch {
    sessionStorage.removeItem(REPORT_TARGETS_CACHE_KEY);
    return null;
  }
};

const writeCachedTargets = (targets: ReportTarget[]) => {
  sessionStorage.setItem(REPORT_TARGETS_CACHE_KEY, JSON.stringify({
    targets,
    cachedAt: Date.now(),
  }));
};

export const readCachedReportTargets = (allowStale = false): ReportTarget[] | null => {
  const payload = parseCachedPayload();
  if (!payload) {
    return null;
  }

  if (!allowStale && Date.now() - payload.cachedAt > REPORT_TARGETS_CACHE_TTL_MS) {
    return null;
  }

  return payload.targets;
};

export const clearCachedReportTargets = () => {
  sessionStorage.removeItem(REPORT_TARGETS_CACHE_KEY);
  inFlightReportTargetsRequest = null;
};

export const fetchReportTargets = async (options?: { forceRefresh?: boolean }): Promise<ReportTarget[]> => {
  const forceRefresh = options?.forceRefresh === true;
  const cached = forceRefresh ? null : readCachedReportTargets(false);
  if (cached) {
    return cached;
  }

  if (!inFlightReportTargetsRequest) {
    inFlightReportTargetsRequest = reportAPI.listTargets()
      .then((response) => sortTargets(response.targets || []))
      .then((targets) => {
        writeCachedTargets(targets);
        return targets;
      })
      .finally(() => {
        inFlightReportTargetsRequest = null;
      });
  }

  return inFlightReportTargetsRequest;
};

export const prefetchReportTargets = async (): Promise<void> => {
  if (readCachedReportTargets(false)) {
    return;
  }

  await fetchReportTargets();
};